// useDeckSyncChannel — Abgleich zwischen Editor-Fenster und Katalog-Zweitfenster ohne
// Server: Wer ein Deck verändert, ruft nach der Bestätigung des Servers
// `announceDeckChanged(uiDeckId)`; jedes andere Fenster auf demselben Deck holt daraufhin
// dieselben Abfragen frisch wie beim Fokus-Refetch. Ohne das sieht ein Fenster auf dem
// zweiten Monitor eine drüben hinzugefügte Karte erst, wenn man hinüberklickt.
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { invalidateDeckViewsAfterRemoteChange } from './deckViewInvalidation';

const CHANNEL_NAME = 'manabox-deck-sync';
const MESSAGE_TYPE = 'deck-changed';

function createWindowId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `deck-sync-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// Kennung dieses Fensters. Ein BroadcastChannel stellt zwar nicht an das sendende
// Kanal-Objekt zu, wohl aber an jedes andere Kanal-Objekt DESSELBEN Fensters — der
// Empfänger-Kanal des Hooks bekäme sonst jede eigene Meldung und würde nach jeder
// eigenen Mutation zusätzlich alles neu holen.
const WINDOW_ID = createWindowId();

let senderChannel = null;

function getSenderChannel() {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!senderChannel) senderChannel = new BroadcastChannel(CHANNEL_NAME);
  return senderChannel;
}

/**
 * Meldet den anderen Fenstern, dass sich dieses Deck geändert hat. Gehört neben die
 * Invalidierung der eigenen Caches und damit hinter die Server-Antwort — ein rein
 * optimistischer Stand ist noch keine Änderung, die ein anderes Fenster laden könnte.
 *
 * @param {string|null} uiDeckId - UI-ID des Decks („virtual_25")
 */
export function announceDeckChanged(uiDeckId) {
  if (uiDeckId == null) return;
  const channel = getSenderChannel();
  if (!channel) return;
  try {
    channel.postMessage({ type: MESSAGE_TYPE, deckId: String(uiDeckId), source: WINDOW_ID });
  } catch {
    // Der Abgleich ist Komfort — schlägt das Senden fehl, bleibt der Fokus-Refetch als
    // Netz. Die Mutation, aus der heraus gemeldet wird, darf daran nicht scheitern.
  }
}

/**
 * Empfangsseite: in BEIDEN Fenstern mounten (Editor-Route und Katalog-Seite). Fehlt
 * `BroadcastChannel` (alte Browser), tut der Hook nichts — der Fokus-Refetch bleibt.
 *
 * @param {Object} options
 * @param {string|null} options.uiDeckId - UI-ID des angezeigten Decks; nur Meldungen zu
 *   genau diesem Deck lösen etwas aus.
 * @param {number|null} [options.numericDeckId] - numerische ID für die Kategorie-Query.
 */
export function useDeckSyncChannel({ uiDeckId, numericDeckId } = {}) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined;
    if (uiDeckId == null) return undefined;

    const channel = new BroadcastChannel(CHANNEL_NAME);
    const handleMessage = (event) => {
      const message = event?.data;
      if (!message || message.type !== MESSAGE_TYPE) return;
      if (message.source === WINDOW_ID) return;
      if (String(message.deckId) !== String(uiDeckId)) return;
      invalidateDeckViewsAfterRemoteChange(queryClient, numericDeckId);
    };
    channel.addEventListener('message', handleMessage);
    return () => {
      channel.removeEventListener('message', handleMessage);
      channel.close();
    };
  }, [queryClient, uiDeckId, numericDeckId]);
}

export default useDeckSyncChannel;
